/* Cut to length (snippets/cut-service.liquid).
 * Splits the selected length into equal pieces (shown as one length, ±5 mm) or the customer's own lengths (the last piece is
 * whatever remains), shows the cuts and price, and writes the result to the hidden fit_cut field:
 *   {"id": <cut service variant>, "cuts": <cuts per length>, "properties": {...}}
 * product-form.js adds it as one cart line, quantity = cuts × number of lengths, and tags the product's own
 * line with "Cut into: …" and the same hidden _cut_ref, so the two lines are connected.
 * In the cart, "Edit cuts" opens a pop-up with the same controls; saving updates the cuts line (pieces,
 * quantity) and the product line's "Cut into". Without JavaScript the link opens the product page instead
 * (?cut_lengths=…&cut_edit=<line key>), and the old cuts line is removed once the new one is added.
 * Loaded on every page (layout/theme.liquid).
 */
(() => {
  if (window.__cutService) return;
  window.__cutService = true;

  const params = new URLSearchParams(location.search);

  const money = (box, cents) =>
    new Intl.NumberFormat(box.dataset.locale || undefined, { style: "currency", currency: box.dataset.currency })
      .format(cents / 100);

  const productQuantity = (box) => {
    if (box.dataset.lengths) return Math.max(1, Number(box.dataset.lengths) || 1); // cart editor
    const input = document.querySelector(`input[name="quantity"][form="${box.dataset.form}"]`);
    return Math.max(1, parseInt(input?.value, 10) || 1);
  };

  // Cutting tolerance, shown with every size.
  const TOLERANCE = "(±5mm)";

  // n equal pieces from total, rounded to the mm (the odd mm is within the tolerance).
  function equalPieces(total, n) {
    return Array.from({ length: n }, () => Math.round(total / n));
  }

  // Saved on the cart lines: "9 × 722mm (±5mm)" or "2000mm, 1500mm, 3000mm (±5mm)"
  function describe(pieces, equal) {
    if (!equal) return `${pieces.map((p) => `${p}mm`).join(", ")} ${TOLERANCE}`;
    return `${pieces.length} × ${pieces[0]}mm ${TOLERANCE}`;
  }

  // Shown on the page: "4 pieces of 1625mm" or "200mm, 500mm and the remaining 5800mm"
  function sayPieces(result) {
    if (result.equal) return `${result.pieces.length} pieces of ${result.pieces[0]}mm`;
    const sizes = result.pieces.map((p) => `${p}mm`);
    if (result.remaining) sizes[sizes.length - 1] = `the remaining ${result.remaining}mm`;
    return sizes.length > 1 ? `${sizes.slice(0, -1).join(", ")} and ${sizes[sizes.length - 1]}` : sizes[0];
  }

  function read(box) {
    const total = Number(box.dataset.total);
    const min = Number(box.dataset.min) || 1;
    const max = Number(box.dataset.max) || 20;
    const mode = box.querySelector('.cut-service__modes input:checked')?.value || "equal";

    if (mode === "equal") {
      const input = box.querySelector("[data-cut-pieces]");
      const n = Math.min(max, Math.max(2, parseInt(input.value, 10) || 2));
      const pieces = equalPieces(total, n);
      if (pieces[pieces.length - 1] < min) return { error: `That makes pieces under ${min}mm – try fewer pieces.` };
      return { pieces, equal: true };
    }

    const entered = [...box.querySelectorAll(".cut-service__length")]
      .map((input) => parseInt(input.value, 10))
      .filter((n) => n > 0);
    if (!entered.length) return { error: "" };
    if (entered.some((n) => n < min)) return { error: `Pieces need to be at least ${min}mm.` };
    const used = entered.reduce((a, b) => a + b, 0);
    const remaining = total - used;
    if (remaining < 0) return { error: `That's ${used}mm in total – longer than the ${total}mm ${box.dataset.measure}.` };
    const pieces = remaining > 0 ? [...entered, remaining] : entered;
    if (remaining > 0 && remaining < min) return { error: `That leaves a ${remaining}mm offcut – too short to cut. Change a size so it's at least ${min}mm, or uses the full ${total}mm.` };
    if (pieces.length < 2) return { error: "" };
    if (pieces.length > max) return { error: `We can cut up to ${max} pieces from each ${box.dataset.measure}.` };
    return { pieces, equal: false, remaining };
  }

  function update(box) {
    const toggle = box.querySelector(".cut-service__toggle");
    const field = box.querySelector("[data-cut-field]");
    const summary = box.querySelector("[data-cut-summary]");
    const error = box.querySelector("[data-cut-error]");
    const mode = box.querySelector('.cut-service__modes input:checked')?.value || "equal";
    box.querySelector(".cut-service__equal").hidden = mode !== "equal";
    box.querySelector(".cut-service__custom").hidden = mode !== "custom";

    // On the product page, "My own sizes" errors wait until the customer has finished a size (left the
    // box) or tried to add to cart, so they don't show on first open or mid-typing ("2" on the way to "2000").
    const showErrors = mode === "equal" || !box.dataset.form || box.dataset.showErrors === "1";
    const result = read(box);
    error.hidden = !(result.error && showErrors);
    error.textContent = showErrors ? result.error || "" : "";

    if (!result.pieces) {
      summary.textContent = "";
      field.disabled = true;
      field.value = "";
      if (box.dataset.form) {
        if (toggle.checked && !result.error && showErrors) {
          error.hidden = false;
          error.textContent = `Enter the sizes you need, or untick ${box.querySelector(".cut-service__title")?.textContent || "cutting"}.`;
        }
        setBlocked(box, toggle.checked);
      }
      return;
    }
    if (box.dataset.form) setBlocked(box, false);

    const cuts = result.pieces.length - 1;
    const lengths = productQuantity(box);
    const totalCuts = cuts * lengths;
    const text = describe(result.pieces, result.equal);
    const measure = box.dataset.measure;
    const cutsText = lengths > 1
      ? `${cuts} cut${cuts > 1 ? "s" : ""} on each of your ${lengths} ${measure}s – ${totalCuts} cuts`
      : `${cuts} cut${cuts > 1 ? "s" : ""}`;
    summary.innerHTML =
      `<strong>${sayPieces(result)}</strong> ${TOLERANCE} · ${cutsText}, ${money(box, Number(box.dataset.price) * totalCuts)}`;

    if (!box.dataset.ref) box.dataset.ref = Math.random().toString(36).slice(2, 10);
    field.disabled = !toggle.checked;
    field.value = JSON.stringify({
      id: Number(box.dataset.variant),
      cuts,
      properties: {
        Cut: box.dataset.for,
        Pieces: text,
        "Cuts per length": String(cuts),
        Lengths: String(lengths),
        _cut_service: "true",
        _cut_ref: box.dataset.ref,
        _cut_lengths: result.pieces.join(","),
        _cut_mode: result.equal ? "equal" : "custom",
        _cut_total: box.dataset.total,
        _cut_min: box.dataset.min,
        _cut_max: box.dataset.max,
        _cut_measure: box.dataset.measure,
        _edit_url: box.dataset.url,
      },
      // Added to the product's own line, so the cart shows which pipe/post the cuts are for.
      main: { "Cut into": text, _cut_ref: box.dataset.ref },
    });
  }

  // While "Cut to length" is ticked but the cuts aren't valid, the product can't be added to the cart:
  // the add button stays clickable and the submit guard below stops the add and shows the reason.
  function setBlocked(box, blocked) {
    box.dataset.cutBlocked = blocked ? "1" : "";
  }

  const updateAll = () => document.querySelectorAll("[data-cut-service]").forEach(update);
  const boxOf = (el) => el.closest?.("[data-cut-service]");

  document.addEventListener("click", (event) => {
    const box = boxOf(event.target);
    if (!box) return;
    const step = event.target.closest("[data-cut-step]");
    if (step) {
      const input = box.querySelector("[data-cut-pieces]");
      const max = Number(box.dataset.max) || 20;
      input.value = Math.min(max, Math.max(2, (parseInt(input.value, 10) || 2) + Number(step.dataset.cutStep)));
      box.querySelector(".cut-service__toggle").checked = true;
      update(box);
    }
    if (event.target.closest("[data-cut-add]")) {
      const list = box.querySelector("[data-cut-list]");
      const item = list.lastElementChild.cloneNode(true);
      const input = item.querySelector("input");
      input.value = "";
      input.setAttribute("aria-label", `Piece ${list.children.length + 1} length (mm)`);
      list.append(item);
      input.focus();
    }
  });

  ["input", "change"].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        const box = boxOf(event.target);
        if (box) {
          if (event.target.matches(".cut-service__length, [data-cut-pieces]")) {
            box.querySelector(".cut-service__toggle").checked = true;
          }
          update(box);
        } else if (event.target.name === "quantity") {
          updateAll();
        }
      },
      true,
    ),
  );

  // Leaving a size box that has something in it shows any problem from then on.
  document.addEventListener("focusout", (event) => {
    const box = boxOf(event.target);
    if (box && event.target.matches(".cut-service__length") && event.target.value !== "") {
      box.dataset.showErrors = "1";
      update(box);
    }
  });

  // The product section re-renders on variant change; refresh the new box.
  document.addEventListener("product:variant-change", () => setTimeout(updateAll, 0));
  new MutationObserver(() => {
    document.querySelectorAll("[data-cut-service]:not([data-cut-ready])").forEach((box) => {
      box.dataset.cutReady = "1";
      update(box);
    });
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });

  // Editing from the cart: pre-fill the lengths, and drop the old cuts line after the new one is added.
  function prefill(box, lengths) {
    const sum = lengths.reduce((a, b) => a + b, 0);
    if (!lengths.length || Math.abs(sum - Number(box.dataset.total)) > lengths.length) return;
    box.querySelector(".cut-service__toggle").checked = true;
    const equal = Math.max(...lengths) - Math.min(...lengths) <= 1;
    box.querySelector(`.cut-service__modes input[value="${equal ? "equal" : "custom"}"]`).checked = true;
    if (equal) {
      box.querySelector("[data-cut-pieces]").value = lengths.length;
    } else {
      const list = box.querySelector("[data-cut-list]");
      const template = list.firstElementChild;
      list.replaceChildren(
        ...lengths.map((len, i) => {
          const item = template.cloneNode(true);
          item.querySelector("input").value = len;
          item.querySelector("input").setAttribute("aria-label", `Piece ${i + 1} length (mm)`);
          return item;
        }),
      );
    }
    update(box);
  }

  const editKey = params.get("cut_edit");
  if (editKey) {
    document.addEventListener("cart:item-add", () => {
      fetch(`${window.routes?.cart_change_url || "/cart/change"}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: editKey, quantity: 0 }),
      })
        .then(() => document.querySelector("mini-cart")?.updateSectionContents?.())
        .catch(() => {});
      const url = new URL(location.href);
      url.searchParams.delete("cut_edit");
      url.searchParams.delete("cut_lengths");
      history.replaceState({}, "", url);
    }, { once: true });
  }


  // ---- Cart: "Edit cuts" pop-up -------------------------------------------------------------
  const cartChange = (body) =>
    fetch(`${window.routes?.cart_change_url || "/cart/change"}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then((response) => (response.ok ? response.json() : Promise.reject(response)));

  const refreshCart = () =>
    Promise.all(
      [...document.querySelectorAll("cart-items, mini-cart")].map((el) => el.updateSectionContents?.()),
    );

  function editorBox(props, price) {
    const dialog = document.createElement("dialog");
    dialog.className = "cut-editor";
    dialog.setAttribute("aria-labelledby", "CutEditorTitle");
    const measure = props._cut_measure || "length";
    dialog.innerHTML = `
      <div class="cut-editor__inner">
        <h2 class="cut-editor__title" id="CutEditorTitle">Change your cuts</h2>
        <p class="cut-editor__for"></p>
        <div class="cut-service" data-cut-service data-cut-ready="1">
          <input type="checkbox" class="cut-service__toggle" checked hidden>
          <div class="cut-service__body">
            <fieldset class="cut-service__modes">
              <legend class="visually-hidden">How to cut</legend>
              <label><input type="radio" name="cut_mode_editor" value="equal" checked> Equal pieces</label>
              <label><input type="radio" name="cut_mode_editor" value="custom"> My own sizes</label>
            </fieldset>
            <div class="cut-service__equal">
              <span class="cut-service__label">How many pieces from each ${measure}?</span>
              <div class="fence-system__qty" role="group" aria-label="Pieces per ${measure}">
                <button type="button" class="fence-system__qty-button" data-cut-step="-1" aria-label="Fewer pieces">−</button>
                <input type="number" class="fence-system__qty-input" data-cut-pieces min="2" value="2" inputmode="numeric" aria-label="Pieces">
                <button type="button" class="fence-system__qty-button" data-cut-step="1" aria-label="More pieces">+</button>
              </div>
            </div>
            <div class="cut-service__custom" hidden>
              <span class="cut-service__label">Sizes you need (mm)</span>
              <ol class="cut-service__list" data-cut-list>
                <li><input type="number" class="cut-service__length" step="1" inputmode="numeric" placeholder="mm" aria-label="Piece 1 length (mm)"></li>
              </ol>
              <button type="button" class="cut-service__add" data-cut-add>+ Another size</button>
            </div>
            <p class="cut-service__summary" data-cut-summary aria-live="polite"></p>
            <p class="cut-service__error" data-cut-error role="alert" hidden></p>
          </div>
          <input type="hidden" data-cut-field disabled>
        </div>
        <div class="cut-editor__actions">
          <button type="button" class="button button-secondary" data-cut-cancel>Cancel</button>
          <button type="button" class="button" data-cut-save>Save</button>
        </div>
      </div>`;
    dialog.querySelector(".cut-editor__for").textContent = (props.Cut || props["Cut from"] || "");
    const box = dialog.querySelector("[data-cut-service]");
    Object.assign(box.dataset, {
      total: props._cut_total, min: props._cut_min || 1, max: props._cut_max || 20, measure,
      price, variant: "", for: (props.Cut || props["Cut from"] || ""), url: props._edit_url || "", ref: props._cut_ref || "",
      currency: window.Shopify?.currency?.active || "AUD",
      locale: `${window.Shopify?.locale || "en"}-${window.Shopify?.country || "AU"}`,
    });
    box.querySelector("[data-cut-pieces]").max = box.dataset.max;
    return { dialog, box };
  }

  async function openEditor(link) {
    let props;
    try {
      props = JSON.parse(link.dataset.cutProps || "{}");
    } catch (error) {
      return false;
    }
    // Liquid's `item.properties | json` gives [[name, value], …]; turn it into {name: value}.
    if (Array.isArray(props)) props = Object.fromEntries(props);
    // Lines added before the length was stored: the pieces add up to it (give or take a mm per piece for equal cuts).
    const pieces = (props._cut_lengths || "").split(",").map(Number).filter(Boolean);
    if (!props._cut_total) {
      if (!pieces.length) return false; // nothing to edit from: use the product page
      const sum = pieces.reduce((a, b) => a + b, 0);
      // Equal pieces are rounded to the mm; stock lengths are multiples of 25 mm (1625, 2900, 6500…).
      props._cut_total = String(props._cut_mode === "equal" ? Math.round(sum / 25) * 25 : sum);
    }
    const key = link.dataset.cutKey;
    const { dialog, box } = editorBox(props, link.dataset.cutPrice || "0");
    // Inside the mini cart drawer, so the theme's "click outside the drawer closes it" doesn't fire.
    (link.closest(".drawer") || document.body).append(dialog);
    dialog.addEventListener("keyup", (event) => event.key === "Escape" && event.stopPropagation());

    const cart = await fetch(`${window.routes?.cart_url || "/cart"}.js`).then((r) => r.json());
    const cutLine = cart.items.find((item) => item.key === key);
    const productLine = props._cut_ref
      ? cart.items.find((item) => item.key !== key && pairKey(item) === pairKey(cutLine || { properties: props }))
      : null;
    const perLength = Number(props["Cuts per length"]) || 1;
    box.dataset.lengths = productLine ? productLine.quantity : Math.max(1, Math.round((cutLine?.quantity || perLength) / perLength));
    box.dataset.variant = String(cutLine?.variant_id || "");

    prefill(box, (props._cut_lengths || "").split(",").map(Number).filter(Boolean));
    update(box);

    const close = () => {
      dialog.close();
      dialog.remove();
    };
    dialog.addEventListener("cancel", close);
    dialog.querySelector("[data-cut-cancel]").addEventListener("click", close);
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) close(); // backdrop
    });
    dialog.querySelector("[data-cut-save]").addEventListener("click", async (event) => {
      const value = box.querySelector("[data-cut-field]").value;
      if (!value) return;
      const data = JSON.parse(value);
      event.target.disabled = true;
      event.target.textContent = "Saving…";
      setCartLoading(true);
      try {
        const lengths = Number(box.dataset.lengths) || 1;
        await cartChange({ id: key, quantity: data.cuts * lengths, properties: data.properties });
        if (productLine) {
          await cartChange({ id: productLine.key, quantity: productLine.quantity, properties: { ...productLine.properties, ...data.main } });
        }
        await refreshCart();
        setCartLoading(false);
        close();
      } catch (error) {
        setCartLoading(false);
        event.target.disabled = false;
        event.target.textContent = "Save";
        box.querySelector("[data-cut-error]").hidden = false;
        box.querySelector("[data-cut-error]").textContent = "Sorry, that didn't save. Please try again.";
      }
    });
    dialog.showModal();
    return true;
  }

  // A pipe line and its cuts line share a hidden link and the same sizes ("Cut into" = "Pieces"). The sizes
  // keep pairs apart in carts where two adds shared one link (fixed since, but those carts still exist).
  const pairKey = (item) => {
    const props = item.properties || {};
    if (!props._cut_ref) return "";
    return `${props._cut_ref}|${props._cut_service === "true" ? props.Pieces : props["Cut into"]}`;
  };

  // The same dim and spinner as the pallet sync, under its own class so either finishing doesn't end the other's.
  // Checkout is held by CSS (custom.css), as the pallet calculator re-enables the button when it finishes.
  function setCartLoading(on) {
    document.querySelectorAll("mini-cart .mini-cart-wrap, #main-cart-items").forEach((el) => el.classList.toggle("cut-updating", on));
  }

  // Keep each pipe/post line and its cuts line in step after any change in the cart (the theme publishes
  // cart:update after a quantity change or removal):
  //   product line removed      → remove its cuts line
  //   cuts line removed         → drop "Cut into" (and the link) from the product line
  //   product quantity changed  → cuts = cuts per length × product quantity
  async function syncCutLines(cart) {
    if (!cart?.items) cart = await fetch(`${window.routes?.cart_url || "/cart"}.js`).then((r) => r.json());
    const groups = new Map();
    cart.items.forEach((item) => {
      const key = pairKey(item);
      if (!key) return;
      const group = groups.get(key) || { cuts: null, products: [] };
      if (item.properties._cut_service === "true") group.cuts = item;
      else group.products.push(item);
      groups.set(key, group);
    });

    const changes = [];
    groups.forEach(({ cuts, products }) => {
      if (cuts && !products.length) {
        changes.push({ id: cuts.key, quantity: 0 });
      } else if (!cuts) {
        products.forEach((item) => {
          // Shopify ignores an empty properties object, so the link is blanked rather than deleted.
          const properties = { ...item.properties, _cut_ref: "" };
          delete properties["Cut into"];
          changes.push({ id: item.key, quantity: item.quantity, properties });
        });
      } else {
        const perLength = Number(cuts.properties["Cuts per length"]) || 1;
        const wanted = perLength * products.reduce((sum, item) => sum + item.quantity, 0);
        const lengths = String(wanted / perLength);
        if (wanted !== cuts.quantity || cuts.properties.Lengths !== lengths) {
          changes.push({ id: cuts.key, quantity: wanted, properties: { ...cuts.properties, Lengths: lengths } });
        }
      }
    });

    if (!changes.length) return;
    setCartLoading(true);
    try {
      for (const change of changes) await cartChange(change);
      await refreshCart();
    } finally {
      setCartLoading(false);
    }
  }

  let syncing = false;
  const queueSync = (cart) => {
    if (syncing) return;
    syncing = true;
    syncCutLines(cart)
      .catch(() => {})
      .finally(() => {
        syncing = false;
      });
  };
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => queueSync(event?.cartData));
    // Each add gets its own link, so adding the same pipe again makes a separate pair of lines.
    subscribe(PUB_SUB_EVENTS.cartItemAdd, () =>
      document.querySelectorAll("[data-cut-service][data-form]").forEach((box) => {
        delete box.dataset.ref;
        update(box);
      }),
    );
  }

  document.addEventListener("click", (event) => {
    const link = event.target.closest?.("[data-cut-edit]");
    if (!link || typeof HTMLDialogElement === "undefined") return;
    event.preventDefault();
    openEditor(link).then((opened) => {
      if (!opened) location.href = link.href;
    });
  });

  document.addEventListener(
    "submit",
    (event) => {
      const box = [...document.querySelectorAll("[data-cut-service][data-form]")].find(
        // getAttribute: the product form has an input named "id", which shadows form.id
        (b) => b.dataset.form === event.target.getAttribute?.("id") && b.dataset.cutBlocked,
      );
      if (!box) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      box.dataset.showErrors = "1";
      update(box);
      box.querySelector("[data-cut-error]")?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    true,
  );

  const start = () => {
    document.querySelectorAll("[data-cut-service]").forEach((box) => {
      box.dataset.cutReady = "1";
      if (params.get("cut_lengths")) prefill(box, params.get("cut_lengths").split(",").map(Number).filter(Boolean));
      update(box);
    });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
